import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Token inválido:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post(`${API}/auth/login`, formData);

      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success(`Bienvenido, ${userData.full_name}`);
      return true;
    } catch (error) {
      console.error('Error de login:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    toast.info('Sesión cerrada');
  };

  const resetPassword = async (email, newPassword) => {
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        new_password: newPassword
      });
      toast.success('Contraseña actualizada correctamente');
      return true;
    } catch (error) {
      console.error('Error al resetear contraseña:', error);
      toast.error('Error al actualizar la contraseña');
      return false;
    }
  };

  const hasRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const isAdmin = () => hasRole(['admin']);
  const isEditor = () => hasRole(['editor']);
  const isViewer = () => hasRole(['viewer']);
  const isAnalyst = () => hasRole(['analyst']);
  
  // Permisos de acciones
  const canEdit = () => hasRole(['admin', 'editor']); // Puede editar gestiones
  const canView = () => hasRole(['admin', 'editor', 'viewer', 'analyst']); // Puede ver el sistema
  const canCreateCases = () => hasRole(['admin', 'editor']); // Puede crear casos (Analista y Consulta NO)
  const canEditCases = () => hasRole(['admin', 'editor']); // Puede editar casos (Analista y Consulta NO)
  const canPerformActions = () => hasRole(['admin', 'editor', 'analyst']); // Puede realizar acciones en gestiones (Consulta NO)
  
  // Permisos específicos para funciones de ANALISTA (Admin también puede, Editor NO)
  const canRegisterAvance = () => hasRole(['admin', 'analyst']); // Registrar avance
  const canGrantProrroga = () => hasRole(['admin', 'analyst']); // Otorgar prórroga
  const canCalificarInstancia = () => hasRole(['admin', 'analyst']); // Calificar instancia probidad
  const canMarkSeguimiento = () => hasRole(['admin', 'analyst']); // Marcar acciones de seguimiento
  const canMarkImprocedente = () => hasRole(['admin', 'analyst']); // Marcar derivación como improcedente
  const canAnalisisComplementario = () => hasRole(['admin', 'analyst']); // Análisis complementario
  
  // Verificar si el usuario puede ver una gestión específica (para Analista)
  const canViewGestion = (gestion) => {
    if (!user) return false;
    // Admin, Editor y Viewer pueden ver todas
    if (hasRole(['admin', 'editor', 'viewer'])) return true;
    // Analista solo puede ver las asignadas a él
    if (isAnalyst()) {
      return gestion.nombre_asesor_cnc === user.full_name || 
             gestion.nombre_asesor_cnc === user.email ||
             gestion.createdBy === user.email;
    }
    return false;
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    resetPassword,
    hasRole,
    isAdmin,
    isEditor,
    isViewer,
    isAnalyst,
    canEdit,
    canView,
    canCreateCases,
    canEditCases,
    canPerformActions,
    canViewGestion,
    canRegisterAvance,
    canGrantProrroga,
    canCalificarInstancia,
    canMarkSeguimiento,
    canMarkImprocedente,
    canAnalisisComplementario,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export default AuthContext;
